/**
 * S10d — shared annotative-representation resolver for the TYPED scene3d
 * adapters (TEXT / MTEXT / HATCH / INSERT / ATTDEF).
 *
 * # Why one helper (CLAUDE.md ПРИОРИТЕТ #1 — single source of truth)
 *
 * AutoCAD annotative entities carry a *list* of per-CANNOSCALE representations
 * (`annotativeContexts[]` — a discriminated-union array, NOT a boolean/single
 * snapshot). When the user switches the active annotation scale the renderer
 * must draw the representation whose `scaleName` matches, applying its per-scale
 * insertion / rotation / (text height | INSERT scaleX,Y | HATCH pattern) and
 * hiding entities that have no representation for the active scale (per
 * ANNOALLVISIBLE).
 *
 * The per-`kind` dispatch already lives in `resolveCannoscaleContext`
 * (`scene/cannoscale/useCannoscaleResolver.ts`) — the SAME resolver the legacy
 * SVG renderer uses (`blockRender.tsx`, `renderAttribute.tsx`). This helper is
 * the thin typed adapter seam: it pulls the active scale name + ANNOALLVISIBLE
 * flag off the shared `AdapterContext` and calls that one resolver, so every
 * typed adapter resolves annotative contexts identically (no drift, no
 * re-implemented switch).
 *
 * # Golden-parity default
 *
 * Outside the live lift call sites `ctx.activeAnnotationScale` is `undefined`
 * and `ctx.annoAllVisible` is `undefined`. This resolves to
 * `resolveCannoscaleContext(contexts, null, true)`:
 *   - a non-annotative entity → `NON_ANNOTATIVE` (visible, no override);
 *   - an annotative entity with no active scale → `FALLBACK_VISIBLE` (visible,
 *     `activeContext: null`, no override).
 * So an adapter that consults this helper produces byte-identical output to the
 * pre-S10d path whenever no scale is threaded — the golden-parity harness (which
 * builds a default `AdapterContext`) is unaffected.
 */

import type { AnnotScaleContext } from "@dxf-ai-editor/drawing-model";

import {
  resolveCannoscaleContext,
  type ResolvedAnnotation,
} from "../../../scene/cannoscale/useCannoscaleResolver";
import type { AdapterContext } from "../adapterContext";

/**
 * @codex round-24 (annotativeResolve:171) — ONE-TRUTH normalization of the active
 * CANNOSCALE header name. An ABSENT (null/undefined) OR blank / whitespace-only
 * header scale means "no annotation scale is active": a bare `?? null` leaks a
 * whitespace-only string ("   ") through as a truthy-but-UNMATCHABLE scale, which
 * then (a) makes `allowList.includes("   ")` return false and wrongly HIDE an
 * OBJECTSCALE INSERT, and (b) diverges from `resolveCannoscaleContext`, whose
 * `scaleName` guard is falsy for a blank string (no match, entity stays visible).
 *
 * EVERY seam that reads `ctx.activeAnnotationScale` routes the raw header value
 * through THIS single normalizer so a blank scale is uniformly treated as "no
 * active scale" (nothing hidden / no OBJECTSCALE filter):
 *   - {@link resolveTypedAnnotation} (per-context resolver),
 *   - {@link activeAllowListMembership} (OBJECTSCALE allow-list membership test),
 *   - `spatialBuilder.ts` early-out guards (`reprojectAnnotativeStubBounds`,
 *     `filterAnnotativeHiddenStubs`).
 *
 * `resolveCannoscaleContext` matches by EXACT `scaleName`, so a non-blank name is
 * returned VERBATIM (untrimmed) — only a fully blank / whitespace-only name
 * collapses to `null`. This is behaviour-preserving for every non-blank scale
 * (the `resolveTypedAnnotation` inline predicate this replaces returned the raw
 * value untrimmed too).
 */
export function normalizeActiveScale(
  raw: string | null | undefined,
): string | null {
  return raw != null && raw.trim() !== "" ? raw : null;
}

/**
 * Resolve the active-scale annotative representation for a typed entity's
 * `annotativeContexts` list, reading the active scale name + ANNOALLVISIBLE
 * flag off the adapter context.
 *
 * @param contexts - the entity's `annotativeContexts` (may be empty/undefined —
 *                   a non-annotative entity resolves to `NON_ANNOTATIVE`).
 * @param ctx      - the shared adapter context (`activeAnnotationScale` +
 *                   `annoAllVisible`).
 */
export function resolveTypedAnnotation(
  contexts: ReadonlyArray<AnnotScaleContext> | undefined,
  ctx: AdapterContext,
): ResolvedAnnotation {
  // @codex round-23 (annotativeResolve:58) — NORMALIZE a BLANK active scale name
  // to `null`. `?? null` only catches null/undefined, so an EMPTY (or
  // whitespace-only) CANNOSCALE header name ("") previously leaked through as a
  // truthy-for-the-flag but UNMATCHABLE scale: `resolveCannoscaleContext` treats
  // "" as "no scale to match" (its `scaleName` guard is falsy —
  // useCannoscaleResolver.ts:150), while the visibility flag below saw
  // `activeScale !== null` and threaded `annoAllVisible=false` → resolving
  // `(contexts, "", false)` → HIDDEN, wrongly hiding EVERY annotative
  // TEXT/MTEXT/HATCH/INSERT the moment the header CANNOSCALE is blank and
  // ANNOALLVISIBLE is false. Trimming a blank name to `null` routes it down the
  // "no filter without a scale" path and aligns this seam with every OTHER
  // consumer of `ctx.activeAnnotationScale` (spatialBuilder.ts:281/377,
  // `activeAllowListMembership` below), all of which already treat a falsy scale
  // as "no active scale" via a truthiness guard. Round-24: the inline blank→null
  // predicate is now the shared `normalizeActiveScale` one-truth normalizer so
  // `activeAllowListMembership` and the spatial-builder guards cannot drift.
  const activeScale = normalizeActiveScale(ctx.activeAnnotationScale);
  return resolveCannoscaleContext(
    contexts ?? [],
    activeScale,
    // @codex round-22 (annotativeResolve:61) — a null/undefined (or, per round-23,
    // blank) active CANNOSCALE means "no annotative filter is active": an
    // annotative entity that carries a context list but has no active scale to
    // match against must stay VISIBLE, REGARDLESS of ANNOALLVISIBLE. This is the
    // AdapterContext contract (an unset `activeAnnotationScale` "never hides
    // anything") and the `useVisibleEntities` fast path. Only once a scale IS
    // active does the header ANNOALLVISIBLE flag gate a no-match entity. Threading
    // the raw `annoAllVisible` when the scale is absent would resolve
    // `(contexts, null, false)` → HIDDEN and wrongly hide EVERY annotative
    // TEXT/MTEXT/HATCH/INSERT the moment CANNOSCALE is unset while ANNOALLVISIBLE
    // happens to be `false` (the stage prop is optional).
    activeScale === null ? true : (ctx.annoAllVisible ?? true),
  );
}

/**
 * @codex round-22 (SceneContent:283) — the active annotation scale that governs
 * the entities rendered DIRECTLY in the currently active space.
 *
 * AutoCAD resolves an annotative entity's per-scale representation against the
 * ACTIVE SPACE's annotation scale, NOT unconditionally against the document
 * header CANNOSCALE:
 *   - MODEL space active → the header CANNOSCALE applies; annotative model-space
 *     TEXT / MTEXT / HATCH / INSERTs draw their matching per-scale representation.
 *   - a LAYOUT (paper space) active → the layout's OWN entities are paper-space
 *     content measured 1:1 in paper units. The model-space CANNOSCALE must NOT
 *     re-scale, re-position, or (ANNOALLVISIBLE=false) HIDE them, so they resolve
 *     with `scaleName = null` — `resolveCannoscaleContext` then ignores the
 *     `annotativeContexts[]` list and renders the master / paper fields.
 *
 * Returns the header scale name in model space, `null` in a layout.
 *
 * SCOPED (shared-modelScene seam — @codex round-20, SceneContent:271): a layout's
 * model VIEWPORTs show model-space content through `rendering/viewportRenderPass.ts`,
 * which renders the ONE shared modelScene (built from this single AdapterContext)
 * through every VP camera + a per-VP visibility MASK — it cannot re-transform
 * annotative geometry per VP. Faithful per-viewport annotative model rendering
 * (each VP its own annotation scale) is a separate MC-series per-viewport
 * model-render slice; until it lands, a layout's model-VP content inherits this
 * space-level decision rather than a per-VP scale.
 */
export function activeAnnotationScaleForSpace(
  cannoscaleName: string | null | undefined,
  activeSpaceKind: string,
): string | null {
  if (activeSpaceKind === "layout") return null;
  return cannoscaleName ?? null;
}

/**
 * S10d round-19/20/21 (@codex, blockFromEntity:235/245) — the OBJECTSCALE
 * membership test the per-context resolver alone MISSES.
 *
 * `BlockInsertEntity.annotationScales` (model.ts) is the OBJECTSCALE membership
 * list — the AUTHORITATIVE set of scales the INSERT is displayed at (AutoCAD's
 * "Annotative Object Scale" list). The OBJECTSCALE reducer
 * (`state/geometryReducerAnnotative.ts` `objectscale-add` / `objectscale-remove`)
 * mutates it INDEPENDENTLY of the per-scale `annotativeContexts[]` geometry
 * snapshots, so the two can DIVERGE in three documented ways:
 *
 *   1. `objectscale-add` admits a scale name BEFORE ANNOUPDATE seeds a context on
 *      an INSERT with an EMPTY `annotativeContexts` — the resolver reports
 *      `NON_ANNOTATIVE` (visible), and membership agrees (member → visible).
 *   2. `objectscale-add` admits a NEW scale on an INSERT that ALREADY carries
 *      contexts for OTHER scales (round-21 — "Let OBJECTSCALE members survive
 *      missing contexts"). The resolver reports HIDDEN (contexts present, no match
 *      for the new scale), but AutoCAD DISPLAYS the INSERT with master fields
 *      because the active CANNOSCALE IS a member. Membership (`true`) OVERRIDES
 *      the resolver's no-match hide — exactly as case 1 is already visible.
 *   3. `objectscale-remove` drops a scale from `annotationScales` but LEAVES its
 *      old per-scale context in place (round-20 — "Honor OBJECTSCALE removal
 *      despite stale contexts"). The resolver still MATCHES that stale context
 *      (visible), but AutoCAD hides it — membership (`false`), not a leftover
 *      representation, is authoritative.
 *
 * Returns the authoritative membership decision, or `null` when the list does NOT
 * restrict visibility (so the caller defers wholly to the per-context resolver):
 *   - `null`  — empty list (no OBJECTSCALE restriction), ANNOALLVISIBLE=true, or
 *               no active scale (golden parity: a default `AdapterContext`);
 *   - `true`  — active scale IS a member → SHOWN (matched context if any, else
 *               master fields);
 *   - `false` — active scale is NOT a member (non-empty list, ANNOALLVISIBLE=
 *               false) → HIDDEN, even over a stale matched context.
 */
function activeAllowListMembership(
  annotationScales: ReadonlyArray<string> | undefined,
  ctx: AdapterContext,
): boolean | null {
  const allowList = annotationScales ?? [];
  if (allowList.length === 0) return null;
  if (ctx.annoAllVisible ?? true) return null;
  // @codex round-24 (annotativeResolve:171) — normalize BEFORE the OBJECTSCALE
  // membership test with the SAME `normalizeActiveScale` one-truth normalizer
  // `resolveTypedAnnotation` uses. A raw `?? null` left a whitespace-only active
  // scale ("   ") TRUTHY, so `allowList.includes("   ")` returned false and HID
  // the INSERT, while the resolver treated the same blank CANNOSCALE as
  // no-active-scale → VISIBLE. Collapsing a blank name to `null` here makes both
  // paths agree: a whitespace-only active scale is "no active scale" (no filter,
  // INSERT visible).
  const active = normalizeActiveScale(ctx.activeAnnotationScale);
  if (active === null) return null;
  return allowList.includes(active);
}

/**
 * S10d round-19/20/21 — the ONE-TRUTH annotative-INSERT visibility gate,
 * combining the per-scale resolver output with the OBJECTSCALE `annotationScales`
 * allow-list ({@link activeAllowListMembership}). Every INSERT site (top-level
 * flatten, nested-child flatten, cache-direct stub predicate) calls THIS so the
 * "is this INSERT hidden at the active scale" decision never drifts.
 *
 * Explicit OBJECTSCALE membership is AUTHORITATIVE (AutoCAD's "Object Scale"
 * list) and OVERRIDES the resolver:
 *   - member (`true`)      → SHOWN, overriding the resolver's no-match hide (a
 *                            not-yet-seeded per-scale context draws with master
 *                            fields — round-21);
 *   - non-member (`false`) → HIDDEN, even over a stale matched context
 *                            (round-19/20);
 *   - no restriction (`null`) → defer to the resolver's `isVisible` (golden
 *                            parity: default ctx → visible, no override).
 */
export function insertAnnotativeHidden(
  anno: ResolvedAnnotation,
  annotationScales: ReadonlyArray<string> | undefined,
  ctx: AdapterContext,
): boolean {
  const membership = activeAllowListMembership(annotationScales, ctx);
  if (membership !== null) return !membership;
  return !anno.isVisible;
}
